import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Moon, Search, Filter, Loader2, Flame, Droplets, Wind, Mountain, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ELEMENTS = ['Tous', 'Feu', 'Eau', 'Air', 'Terre'];

const SUITS = {
  wands: { name: 'Bâtons', icon: Flame, color: 'orange', bgColor: 'bg-orange-500', borderColor: 'border-orange-300' },
  cups: { name: 'Coupes', icon: Droplets, color: 'blue', bgColor: 'bg-blue-500', borderColor: 'border-blue-300' },
  swords: { name: 'Épées', icon: Wind, color: 'gray', bgColor: 'bg-gray-500', borderColor: 'border-gray-300' },
  pentacles: { name: 'Deniers', icon: Mountain, color: 'green', bgColor: 'bg-emerald-500', borderColor: 'border-emerald-300' }
};

// Component to handle card images with fallback
const CardImage = ({ card }) => {
  const [imageError, setImageError] = useState(false);
  const hasValidImage = card.image_url && !card.image_url.includes('placeholder');
  const showFallback = imageError || !hasValidImage;
  
  const SuitIcon = SUITS[card.suit]?.icon || Moon;
  
  const gradientClass = card.card_type === 'major' ? 'from-purple-100 to-indigo-100' :
    card.suit === 'wands' ? 'from-orange-100 to-amber-100' :
    card.suit === 'cups' ? 'from-blue-100 to-cyan-100' :
    card.suit === 'swords' ? 'from-gray-100 to-slate-100' :
    'from-emerald-100 to-green-100';
  
  const iconClass = card.card_type === 'major' ? 'text-purple-400' :
    card.suit === 'wands' ? 'text-orange-400' :
    card.suit === 'cups' ? 'text-blue-400' :
    card.suit === 'swords' ? 'text-gray-400' :
    'text-emerald-400';

  if (showFallback) {
    return (
      <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${gradientClass}`}>
        {card.card_type === 'major' ? (
          <Moon className={`h-12 w-12 ${iconClass}`} />
        ) : (
          <SuitIcon className={`h-12 w-12 ${iconClass}`} />
        )}
      </div>
    );
  }

  return (
    <img
      src={card.image_url}
      alt={card.name}
      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
      loading="lazy"
      onError={() => setImageError(true)}
    />
  );
};

const MysteriaeArcanesPage = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedElement, setSelectedElement] = useState('Tous');
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'major', 'minor', or suit names
  const [suits, setSuits] = useState({});

  useEffect(() => {
    fetchCards();
    fetchSuits();
  }, []);

  const fetchCards = async () => {
    try {
      const response = await fetch(`${API_URL}/api/mysteriae/cards`);
      const data = await response.json();
      setCards(data.cards);
    } catch (error) {
      console.error('Error fetching cards:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuits = async () => {
    try {
      const response = await fetch(`${API_URL}/api/mysteriae/suits`);
      const data = await response.json();
      setSuits(data.suits);
    } catch (error) {
      console.error('Error fetching suits:', error);
    }
  };

  const filteredCards = cards.filter(card => {
    // Tab filter
    let matchesTab = true;
    if (activeTab === 'major') {
      matchesTab = card.card_type === 'major';
    } else if (activeTab === 'minor') {
      matchesTab = card.card_type === 'minor';
    } else if (['wands', 'cups', 'swords', 'pentacles'].includes(activeTab)) {
      matchesTab = card.suit === activeTab;
    }

    // Search filter
    const matchesSearch = 
      card.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      card.name_en.toLowerCase().includes(searchTerm.toLowerCase()) ||
      card.keywords.some(k => k.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Element filter
    const matchesElement = selectedElement === 'Tous' || card.element === selectedElement;
    
    return matchesTab && matchesSearch && matchesElement;
  });

  const majorCount = cards.filter(c => c.card_type === 'major').length;
  const minorCount = cards.filter(c => c.card_type === 'minor').length;

  const getCardLink = (card) => {
    if (card.card_type === 'major') {
      return `/mysteriae/arcanes/${card.number}`;
    }
    return `/mysteriae/arcanes/${card.card_id}`;
  };

  const getCardBadge = (card) => {
    if (card.card_type === 'major') {
      return (
        <div className="absolute top-2 left-2 w-7 h-7 bg-purple-600 rounded-full flex items-center justify-center shadow-lg">
          <span className="text-white text-xs font-bold">{card.number}</span>
        </div>
      );
    }
    
    const suitInfo = SUITS[card.suit];
    if (suitInfo) {
      const SuitIcon = suitInfo.icon;
      return (
        <div className={`absolute top-2 left-2 w-7 h-7 ${suitInfo.bgColor} rounded-full flex items-center justify-center shadow-lg`}>
          <SuitIcon className="h-4 w-4 text-white" />
        </div>
      );
    }
    return null;
  };

  const getCardBorderColor = (card) => {
    if (card.card_type === 'major') {
      return 'border-purple-200 group-hover:border-purple-400';
    }
    const suitInfo = SUITS[card.suit];
    return suitInfo ? `${suitInfo.borderColor} group-hover:border-${suitInfo.color}-400` : 'border-gray-200';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 via-white to-purple-50">
      {/* Decorative elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-64 h-64 bg-purple-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-indigo-200/20 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-purple-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link 
              to="/mysteriae" 
              className="flex items-center gap-2 text-purple-600 hover:text-purple-700 transition-colors"
              data-testid="back-to-mysteriae"
            >
              <ChevronLeft className="h-5 w-5" />
              <span className="font-semibold">MYSTERIAE</span>
            </Link>
            <span className="text-gray-300">/</span>
            <div className="flex items-center gap-2">
              <Moon className="h-5 w-5 text-purple-600" />
              <span className="font-bold text-gray-900 tracking-wide">Les Arcanes du Tarot</span>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <section className="py-4 px-4 border-b border-purple-100 bg-white/50">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap gap-2">
            {/* Main tabs */}
            <button
              onClick={() => setActiveTab('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                activeTab === 'all'
                  ? 'bg-purple-600 text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-purple-50 border border-gray-200'
              }`}
              data-testid="tab-all"
            >
              <Sparkles className="h-4 w-4" />
              Tous ({cards.length})
            </button>
            <button
              onClick={() => setActiveTab('major')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'major'
                  ? 'bg-purple-600 text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-purple-50 border border-gray-200'
              }`}
              data-testid="tab-major"
            >
              Majeurs ({majorCount})
            </button>
            <button
              onClick={() => setActiveTab('minor')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'minor'
                  ? 'bg-purple-600 text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-purple-50 border border-gray-200'
              }`}
              data-testid="tab-minor"
            >
              Mineurs ({minorCount})
            </button>

            {/* Divider */}
            <div className="w-px h-8 bg-gray-300 mx-2 self-center" />

            {/* Suit tabs */}
            {Object.entries(SUITS).map(([key, suit]) => {
              const SuitIcon = suit.icon;
              const suitCount = cards.filter(c => c.suit === key).length;
              return (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
                    activeTab === key
                      ? `${suit.bgColor} text-white shadow-md`
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                  data-testid={`tab-${key}`}
                >
                  <SuitIcon className="h-4 w-4" />
                  {suit.name} ({suitCount})
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Search and Filters */}
      <section className="py-6 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between mb-6">
            {/* Search */}
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher une carte..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white border border-purple-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
                data-testid="search-input"
              />
            </div>

            {/* Element Filter */}
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <div className="flex gap-2">
                {ELEMENTS.map(element => (
                  <button
                    key={element}
                    onClick={() => setSelectedElement(element)}
                    className={`px-3 py-1.5 text-sm rounded-lg transition-all font-medium ${
                      selectedElement === element
                        ? 'bg-purple-600 text-white shadow-md'
                        : 'bg-white text-gray-600 hover:bg-purple-50 border border-gray-200'
                    }`}
                    data-testid={`filter-${element.toLowerCase()}`}
                  >
                    {element}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Cards Count */}
          <p className="text-gray-600 text-sm mb-6">
            {filteredCards.length} carte{filteredCards.length > 1 ? 's' : ''} trouvée{filteredCards.length > 1 ? 's' : ''}
          </p>
        </div>
      </section>

      {/* Cards Grid */}
      <section className="px-4 pb-16">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 text-purple-600 animate-spin" />
            </div>
          ) : filteredCards.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500">Aucune carte trouvée pour cette recherche.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-5">
              {filteredCards.map((card, index) => (
                <motion.div
                  key={card.card_id || card.number}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(0.03 * index, 0.5) }}
                >
                  <Link
                    to={getCardLink(card)}
                    className="block group"
                    data-testid={`card-${card.card_id || card.number}`}
                  >
                    <div className={`relative overflow-hidden rounded-xl border-2 ${getCardBorderColor(card)} transition-all duration-300 group-hover:shadow-xl group-hover:shadow-purple-200/50 bg-white`}>
                      {/* Card Image */}
                      <div className="aspect-[2/3] overflow-hidden">
                        <CardImage card={card} />
                      </div>
                      
                      {/* Overlay on hover */}
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute bottom-0 left-0 right-0 p-3">
                          <div className="flex items-center gap-2 mb-1">
                            {card.card_type === 'major' && (
                              <span className="text-purple-300 text-xs font-bold">{card.number}</span>
                            )}
                            {card.suit_name_fr && (
                              <span className="text-purple-300 text-xs font-bold">{card.suit_name_fr}</span>
                            )}
                            <span className="text-gray-400 text-xs">•</span>
                            <span className="text-gray-300 text-xs">{card.element}</span>
                          </div>
                          <h3 className="text-white font-semibold text-sm">{card.name}</h3>
                          <p className="text-gray-300 text-xs mt-1 line-clamp-2">
                            {card.keywords.slice(0, 3).join(' • ')}
                          </p>
                        </div>
                      </div>

                      {/* Badge */}
                      {getCardBadge(card)}

                      {/* Type indicator for minor arcana */}
                      {card.card_type === 'minor' && (
                        <div className="absolute top-2 right-2 px-2 py-0.5 bg-white/90 rounded text-xs font-medium text-gray-600">
                          {card.rank === 'ace' && 'As'}
                          {card.rank === 'two' && '2'}
                          {card.rank === 'three' && '3'}
                          {card.rank === 'four' && '4'}
                          {card.rank === 'five' && '5'}
                          {card.rank === 'six' && '6'}
                          {card.rank === 'seven' && '7'}
                          {card.rank === 'eight' && '8'}
                          {card.rank === 'nine' && '9'}
                          {card.rank === 'ten' && '10'}
                          {card.rank === 'page' && 'V'}
                          {card.rank === 'knight' && 'C'}
                          {card.rank === 'queen' && 'R'}
                          {card.rank === 'king' && 'Ro'}
                        </div>
                      )}
                    </div>
                    
                    {/* Card info below (always visible) */}
                    <div className="mt-3 px-1">
                      <h3 className="text-gray-900 text-sm font-semibold truncate">{card.name}</h3>
                      <p className="text-gray-500 text-xs">{card.name_en}</p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default MysteriaeArcanesPage;
